import { Navigate, useLocation } from "react-router";
import { useMarket } from "@ferrivia/i18n";
import { ChatScreen } from "../screens/ChatScreen";
import { HomeScreen } from "../screens/HomeScreen";
import { InfusionScreen } from "../screens/InfusionScreen";
import { ResultsScreen } from "../screens/ResultsScreen";
import { SupportScreen } from "../screens/SupportScreen";
import { tabsFor } from "./routes";
import { useTabNavigation } from "./useTabNavigation";
import s from "./TabPanels.module.css";

const PANEL_COMPONENTS: Record<string, () => React.JSX.Element> = {
  "/": HomeScreen,
  "/infusion": InfusionScreen,
  "/support": SupportScreen,
  "/results": ResultsScreen,
  "/chat": ChatScreen,
};

// Screens that lay out their own header/content/composer and scroll
// internally (see ThreadView) rather than relying on `.panel`'s own scroll
// — see the comment on `.panelSelfScrolling` in TabPanels.module.css.
const SELF_SCROLLING_PATHS = new Set(["/chat"]);

// Every tab screen stays mounted so switching tabs never remounts a screen
// (and its scroll position). Only the active panel is in the a11y tree / tab
// order; the rest are `inert` and visually hidden.
export function TabPanels() {
  const { pathname, search } = useLocation();
  const { registerPanel } = useTabNavigation();
  const market = useMarket();
  const tabs = tabsFor(market);

  // The Store tab is gone, but Stripe sessions opened before it went, and
  // installed PWAs, still return to /store?order=…&token=…. The generic
  // redirect below would drop the query and with it the confirmation.
  if (pathname === "/store") {
    return market === "us" ? <Navigate to="/" replace /> : <Navigate to={{ pathname: "/infusion", search }} replace />;
  }

  if (!tabs.some((tab) => tab.path === pathname)) {
    return <Navigate to="/" replace />;
  }

  return (
    <>
      {tabs.map(({ path }) => {
        const Panel = PANEL_COMPONENTS[path];
        const isActive = path === pathname;
        const panelClass = SELF_SCROLLING_PATHS.has(path) ? s.panelSelfScrolling : s.panel;

        return (
          <div
            key={path}
            // Each panel is its own scroll container, so tapping the tab
            // already showing can scroll this one back to the top.
            ref={(element) => {
              registerPanel(path, element);
            }}
            className={isActive ? panelClass : `${panelClass} ${s.hidden}`}
            aria-hidden={!isActive}
            inert={!isActive}
          >
            <Panel />
          </div>
        );
      })}
    </>
  );
}
